import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    TextField,
    Typography,
} from '@mui/material';

import AdminNodeStore from '../../../stores/admin-node-store';
import { DeviceService } from '../../../http/device-service';
import { DbNode } from '../../../types/node';
import NodePickerItem from '../NodePickerItem';

//=================================================================================================
interface AddDeviceDialogProps {
    open: boolean;
    onClose: () => void;
    onAdded: () => void;
    rootNodeId: number;
}

const AddDeviceDialog = observer(({ open, onClose, onAdded, rootNodeId }: AddDeviceDialogProps) => {
    const [rootStore] = useState(() => new AdminNodeStore(rootNodeId));

    const [deviceId, setDeviceId] = useState('');
    const [selectedNode, setSelectedNode] = useState<DbNode | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            rootStore.loadNode().catch(() => {});
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        if (submitting) return;
        setDeviceId('');
        setSelectedNode(null);
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        const id = Number(deviceId);
        if (!deviceId.trim() || isNaN(id) || id <= 0) {
            setError('Введите корректный номер устройства');
            return;
        }
        if (!selectedNode) {
            setError('Выберите ноду');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await DeviceService.add({ device_id: id, node_id: selectedNode.node_id });
            onAdded();
            handleClose();
        } catch {
            setError('Не удалось добавить устройство');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs"
            onKeyDown={(e) => { if (e.key === 'Enter' && !submitting) handleSubmit(); }}
        >
            <DialogTitle>Добавить устройство</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                <TextField
                    label="Номер устройства"
                    value={deviceId}
                    onChange={(e) => setDeviceId(e.target.value)}
                    type="number"
                    autoFocus
                    fullWidth
                    size="small"
                    slotProps={{ htmlInput: { min: 1 } }}
                />
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Нода</Typography>
                        {selectedNode && (
                            <Chip
                                label={selectedNode.name}
                                size="small"
                                onDelete={() => setSelectedNode(null)}
                            />
                        )}
                    </Box>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 220, overflowY: 'auto' }}>
                        {rootStore.loading && rootStore.items.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <List dense disablePadding sx={{ p: 0.5 }}>
                                {rootStore.items.map((node) => (
                                    <NodePickerItem
                                        key={node.node_id}
                                        node={node}
                                        depth={0}
                                        selectedNodeId={selectedNode?.node_id ?? null}
                                        onSelect={setSelectedNode}
                                    />
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>Отмена</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting} disableElevation>
                    {submitting ? <CircularProgress size={18} /> : 'Добавить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default AddDeviceDialog;
